import { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { connectRedux } from '../../../helpers/connectRedux'
import { RouteComponentProps, withRouter } from '../../../helpers/withRouter'
import { Button, Page, Spinner, Title, ValidatedInput } from '../../../components'
import CountryPickerModal from '../../../containers/CountryPickerModal'
import countries from '../../../countries.json'
import { isEmailValid, isTelephoneValid } from '../../../helpers'
import { appsLogosSelected, appsRecoverPasswordSelected, appsStyleSelected } from '../../../helpers/app'
import { ICountry } from '../../../models/country'
import { LiteralsService } from '../../../services'
import {
  requestRecoverCodeAction,
  requestRecoverCodeFailedAction,
  setRecoverPasswordCallingCodeAction,
  setRecoverPasswordCountryAction,
  setRecoverPasswordEmailAction,
  setRecoverPasswordPhoneAction
} from '../../../store/actions'
import { IStoreState } from '../../../store/states'
import StyledArticle from '../styles'
import { AnyAction } from 'redux'

const isPhoneRecovery = appsRecoverPasswordSelected?.username === 'phone'

interface IProps extends DispatchProp<AnyAction>, RouteComponentProps {
  email: string
  phone: string
  error: string
  isLoading: boolean
  country?: ICountry
  callingCode: string
}

class RequestCodeStep extends PureComponent<IProps> {
  constructor(props: IProps) {
    super(props)
    if (isPhoneRecovery && !props.country) {
      const defaultCountry = countries.find(country => country.code === 'ES')
      if (defaultCountry) {
        props.dispatch(setRecoverPasswordCountryAction(defaultCountry))
        props.dispatch(setRecoverPasswordCallingCodeAction(defaultCountry.dial_code))
      }
    }
  }

  private get buttonIsDisabled(): boolean {
    const { email, phone, isLoading, error } = this.props

    if (isPhoneRecovery) return !isTelephoneValid(phone) || isLoading || !!error
    return !isEmailValid(email) || isLoading || !!error
  }

  private get noUserError(): ReactNode {
    const error = this.props.error
    if (!error) return null
    return (
      <div className="error-container">
        <span className="error">
          { LiteralsService.get(error as any, true) }
        </span>
      </div>
    )
  }

  private get recoverButton(): ReactNode {
    const { isLoading } = this.props
    if (isLoading) return <Spinner />
    return (
      <Button
        title={ LiteralsService.get('next') }
        onPress={ this.handleValidatedButtonPress }
        filled
        upper
        isDisabled={ this.buttonIsDisabled }
      />
    )
  }

  public render(): ReactNode {

    const { email, phone, error, country } = this.props

    return (
      <Page className={ appsStyleSelected?.style }>
        <StyledArticle>
          <form onSubmit={ e => e.preventDefault() }>
            <header>
              <img  className={ appsLogosSelected?.classRounded }
                    src={ appsLogosSelected?.logoRounded }
                    alt={ appsLogosSelected?.alt } />
            </header>
            <Title text={ LiteralsService.get('writeYourEmail', true) } />
            <section>
              {
                isPhoneRecovery ? (
                  <ValidatedInput
                    name="phone"
                    phone
                    label={ LiteralsService.get('phone', true) }
                    value={ phone }
                    country={ country }
                    onChange={ this.onPhoneChange }
                    error={ !isTelephoneValid(phone) || !!error }
                  />
                ) : (
                  <ValidatedInput
                    name="email"
                    label={ LiteralsService.get('email', true) }
                    value={ email }
                    onChange={ this.onEmailChange }
                    error={ !isEmailValid(email) || !!error }
                  />
                )
              }
              { this.noUserError }
            </section>
            { isPhoneRecovery && <CountryPickerModal selectCountry={ this.onCountryChange } /> }
            <section className="actions">
              { this.recoverButton }
            </section>
          </form>
        </StyledArticle>
      </Page>
    )
  }

  private onEmailChange = (email: string): void => {
    const { dispatch, error } = this.props
    if (error) dispatch(requestRecoverCodeFailedAction(''))
    dispatch(setRecoverPasswordEmailAction(email))
  }

  private onPhoneChange = (phone: string): void => {
    const { dispatch, error } = this.props
    if (error) dispatch(requestRecoverCodeFailedAction(''))
    dispatch(setRecoverPasswordPhoneAction(phone))
  }

  private onCountryChange = (country: ICountry): void => {
    const { dispatch } = this.props
    dispatch(setRecoverPasswordCountryAction(country))
    dispatch(setRecoverPasswordCallingCodeAction(country.dial_code))
  }

  private handleValidatedButtonPress = (): void => {
    const { dispatch } = this.props
    dispatch(requestRecoverCodeAction())
  }
}

const mapStateToProps = ({ recoverPassword }: IStoreState) => ({
  email: recoverPassword.email,
  phone: recoverPassword.phone,
  error: recoverPassword.error,
  isLoading: recoverPassword.isLoading,
  country: recoverPassword.country,
  callingCode: recoverPassword.callingCode
})

export default withRouter(connectRedux(mapStateToProps, RequestCodeStep))
